import { styled } from "goober";
import MarkdownToJsx from "markdown-to-jsx";
import { Children, isValidElement, memo, useRef } from "react";
import { Fragment } from "react/jsx-runtime";
import { CopyButton } from "./CopyButton";
import { highlightCode } from "./highlightCode";
import { Link } from "./Link";
import { Mermaid } from "./Mermaid";

const PreWrapper = styled("div")`
  position: relative;
  & > pre {
    position: relative;
    z-index: 0;
  }
  &:hover [data-copy-button] {
    opacity: 1;
  }
`;

const TableWrapper = styled("div")`
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
`;

function Table(props: React.TableHTMLAttributes<HTMLTableElement>) {
  return (
    <TableWrapper>
      <table {...props} />
    </TableWrapper>
  );
}

function Code(props: React.HTMLAttributes<HTMLElement>) {
  const langMatch = /(?:^|\s)lang-(\S+)/.exec(props.className ?? "");
  const lang = langMatch?.[1];
  if (!lang || typeof props.children !== "string") return <code {...props} />;
  return <code {...props}>{highlightCode(props.children, lang)}</code>;
}

function CodeBlock(props: React.HTMLAttributes<HTMLPreElement>) {
  const child = Children.toArray(props.children).find(isValidElement);
  const childProps = child?.props as
    | { className?: string; children?: unknown }
    | undefined;
  if (
    /(?:^|\s)lang-mermaid(?:\s|$)/.test(childProps?.className ?? "") &&
    typeof childProps?.children === "string"
  )
    return <Mermaid source={childProps.children} />;
  return <CodeBlockWithCopy {...props} />;
}

function CodeBlockWithCopy(props: React.HTMLAttributes<HTMLPreElement>) {
  const preRef = useRef<HTMLPreElement>(null);
  return (
    <PreWrapper>
      <pre ref={preRef} {...props} />
      <CopyButton getText={() => preRef.current?.textContent} />
    </PreWrapper>
  );
}

type Props = {
  children: string;
  internalHref?: (href: string) => string | null;
};

function Anchor({
  href,
  children,
  internalHref,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  internalHref?: (href: string) => string | null;
}) {
  const resolved = href ? internalHref?.(href) : null;
  if (resolved)
    return (
      <Link to={resolved} {...rest}>
        {children}
      </Link>
    );
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  );
}

// Re-renders rebuild `options` and defeat markdown-to-jsx's internal compile cache.
export const Markdown = memo(function Markdown({
  children,
  internalHref,
}: Props) {
  "use memo";
  return (
    <MarkdownToJsx
      options={{
        wrapper: Fragment,
        forceBlock: true,
        disableParsingRawHTML: true,
        overrides: {
          a: {
            component: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) =>
              Anchor({ ...props, internalHref }),
          },
          pre: {
            component: CodeBlock,
          },
          code: {
            component: Code,
          },
          table: {
            component: Table,
          },
        },
      }}
    >
      {children}
    </MarkdownToJsx>
  );
});
